import React, { useEffect, useMemo, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { obtenerGastos, crearGasto, eliminarGasto } from '../services/gastosService';
import { obtenerCuentas, iconoTipo } from '../services/cuentasService';
import CampoNumero from './CampoNumero';
import EditarGastoModal from './EditarGastoModal';
import GestionCategoriasGastoModal from './GestionCategoriasGastoModal';
import { obtenerCategoriasGasto } from '../services/categoriasGastoService';
import { mensajeAmigable } from '../utils/errores';
import { aNumero } from '../utils/validacion';
import { useToast } from '../context/ToastContext';
import { CATEGORIAS_GASTO } from '../config';
import { fechaLocal } from '../utils/fecha';

const hoy = () => fechaLocal();

export default function GastosView({ onVolver }) {
  const { mostrarToast } = useToast();
  const [gastos, setGastos] = useState(null);
  const [cuentas, setCuentas] = useState([]);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [confirmandoId, setConfirmandoId] = useState(null);
  const [editandoId, setEditandoId] = useState(null);
  const [categoriasG, setCategoriasG] = useState([]);
  const [gestion, setGestion] = useState(false);
  const [form, setForm] = useState({
    categoria: CATEGORIAS_GASTO[0], descripcion: '', monto: '', cuentaId: '', fecha: hoy(),
  });

  const cargar = async () => {
    setError(null);
    try {
      const [g, cs, cats] = await Promise.all([obtenerGastos(), obtenerCuentas(), obtenerCategoriasGasto()]);
      setGastos(g);
      setCuentas(cs);
      setCategoriasG(cats);
      setForm((f) => ({
        ...f,
        cuentaId: f.cuentaId || (cs[0]?.id || ''),
        categoria: cats.some((c) => c.nombre === f.categoria) ? f.categoria : (cats[0]?.nombre || f.categoria),
      }));
    } catch (e) {
      setError(mensajeAmigable(e));
    }
  };

  useEffect(() => { cargar(); }, []);

  const totalMes = useMemo(() => {
    if (!gastos) return 0;
    const inicioMes = new Date();
    inicioMes.setDate(1);
    const desde = inicioMes.toISOString().slice(0, 10);
    return gastos.filter((g) => g.fecha >= desde).reduce((s, g) => s + Number(g.monto || 0), 0);
  }, [gastos]);

  const agregar = async (e) => {
    e.preventDefault();
    const monto = aNumero(form.monto);
    if (monto === null || monto <= 0) {
      mostrarToast('Escribe el monto del gasto (solo números, mayor que 0)', 'error');
      return;
    }
    if (!form.fecha) { mostrarToast('Elige la fecha del gasto', 'error'); return; }
    if (!form.cuentaId) { mostrarToast('Elige de qué cuenta salió el dinero', 'error'); return; }
    setGuardando(true);
    try {
      await crearGasto(form);
      mostrarToast('Gasto registrado ✅', 'exito');
      setForm({ categoria: categoriasG[0]?.nombre || CATEGORIAS_GASTO[0], descripcion: '', monto: '', cuentaId: form.cuentaId, fecha: hoy() });
      cargar();
    } catch (err) {
      mostrarToast(mensajeAmigable(err), 'error');
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async (id) => {
    try {
      await eliminarGasto(id);
      mostrarToast('Gasto eliminado 🗑️', 'exito');
      setConfirmandoId(null);
      cargar();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    }
  };

  const fmt = (n) => `${simboloMoneda()} ${Number(n).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const nombreCuenta = (id) => cuentas.find((c) => c.id === id)?.nombre || '';
  const input = { width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };

  return (
    <div className="k-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <h2 className="k-page-title">💸 Gastos</h2>
        <div className="k-actions">
          <button onClick={() => setGestion(true)} className="k-btn-ghost">
            🏷️ Categorías
          </button>
          <button onClick={onVolver} className="k-btn-soft">
            ← Volver
          </button>
        </div>
      </div>

      <div className="k-card" style={{ padding: '16px', marginBottom: '16px' }}>
        <p style={{ margin: '0 0 4px', fontSize: '12px', color: 'var(--k-muted)', fontWeight: 600 }}>Gastos de este mes</p>
        <p style={{ margin: 0, fontSize: '24px', fontWeight: 700, fontFamily: "'DM Mono', monospace", color: '#E02424' }}>{fmt(totalMes)}</p>
      </div>

      <form onSubmit={agregar} style={{ backgroundColor: '#FFF5F7', borderRadius: '12px', padding: '16px', border: '1px solid #FBCFE8', marginBottom: '20px' }}>
        <p style={{ margin: '0 0 12px', fontWeight: 'bold', color: 'var(--color-primario,#DB2777)' }}>➕ Nuevo gasto</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '10px' }}>
          <div>
            <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Categoría</label>
            <select value={form.categoria} disabled={guardando} onChange={(e) => setForm({ ...form, categoria: e.target.value })} style={input}>
              {categoriasG.map((c) => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
              {form.categoria && !categoriasG.some((c) => c.nombre === form.categoria) && <option value={form.categoria}>{form.categoria}</option>}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Monto (L)</label>
            <CampoNumero valor={form.monto} onChange={(v) => setForm({ ...form, monto: v })} deshabilitado={guardando}
              min={0} prefijo="L" placeholder="0.00" estilo={input} />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Sale de la cuenta</label>
            <select value={form.cuentaId} disabled={guardando} onChange={(e) => setForm({ ...form, cuentaId: e.target.value })} style={input}>
              {cuentas.length === 0 && <option value="">Sin cuentas — crea una en 🏦 Cuentas</option>}
              {cuentas.map((c) => <option key={c.id} value={c.id}>{iconoTipo(c.tipo)} {c.nombre}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Fecha</label>
            <input type="date" value={form.fecha} disabled={guardando} onChange={(e) => setForm({ ...form, fecha: e.target.value })} style={input} />
          </div>
        </div>
        <div style={{ marginBottom: '10px' }}>
          <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Descripción (opcional)</label>
          <input value={form.descripcion} disabled={guardando} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} style={input} placeholder="Ej. Pago de luz de julio" />
        </div>
        <button type="submit" disabled={guardando}
          style={{ padding: '10px 24px', backgroundColor: guardando ? '#9CA3AF' : 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: guardando ? 'wait' : 'pointer' }}>
          {guardando ? '⏳ Guardando...' : 'Registrar gasto'}
        </button>
      </form>

      {!gastos && !error && (
        <div style={{ textAlign: 'center', padding: '32px', color: '#6B7280' }}>⏳ Cargando gastos...</div>
      )}
      {error && (
        <p style={{ textAlign: 'center', padding: '24px', color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</p>
      )}
      {gastos && gastos.length === 0 && (
        <p style={{ textAlign: 'center', padding: '32px', color: '#9CA3AF' }}>Aún no has registrado gastos.</p>
      )}
      {gastos && gastos.length > 0 && (
        <div className="k-card" style={{ overflow: 'hidden' }}>
          {gastos.map((g) => (
            <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--k-hairline)', gap: '8px' }}>
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontWeight: 600, color: 'var(--k-ink)', fontSize: '14px' }}>
                  {g.categoria}{g.descripcion ? ` · ${g.descripcion}` : ''}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
                  {g.fecha}{nombreCuenta(g.cuenta_id) ? ` · ${nombreCuenta(g.cuenta_id)}` : ''}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                <span style={{ fontWeight: 'bold', color: '#DC2626' }}>{fmt(g.monto)}</span>
                {confirmandoId === g.id ? (
                  <>
                    <button onClick={() => borrar(g.id)} style={{ padding: '4px 8px', backgroundColor: '#DC2626', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>Sí</button>
                    <button onClick={() => setConfirmandoId(null)} style={{ padding: '4px 8px', backgroundColor: '#E5E7EB', color: '#374151', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>No</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => setEditandoId(g.id)} title="Editar"
                      style={{ padding: '4px 8px', background: 'none', border: '1px solid #D1D5DB', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>✏️</button>
                    <button onClick={() => setConfirmandoId(g.id)} title="Eliminar"
                      style={{ padding: '4px 8px', background: 'none', border: '1px solid #FCA5A5', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>🗑️</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editandoId && (
        <EditarGastoModal referencia={editandoId} onCerrar={() => setEditandoId(null)} onGuardado={cargar} />
      )}

      {gestion && (
        <GestionCategoriasGastoModal onCerrar={() => setGestion(false)} onCambio={cargar} />
      )}
    </div>
  );
}
